import { addToast, Button, Icon, Table } from '@octano/global-ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';

import TableEmptyContent from '../../../../../../components/text/TableEmptyContent';
import { PathsLayouts } from '../../../../../../config/routes';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { downloadFromBlob } from '../../../../../../utils/blob';
import HeaderInfo from '../../parts/Header/HeaderInfo';
import { getReport } from '../api';
import useActions from '../hooks/useActions';
import useColumns from '../hooks/useColumns';
import { useSharedData } from '../hooks/useSharedData';
import {
  TOTAL_ITEMS_PER_PAGE,
  useAssignedStudentsTableLoader,
} from './AssignedStudensTableLoader';
import AssignedStudentFilters from './AssignedStudentFilters';
import AssignedStudentModal, {
  AssignedStudentModalRef,
} from './AssignedStudentModal/AssignedStudentModal';
import AssignedStudentFiltersLoader from './AssignedStudentsBaseLoader';
import ConfirmModal, { ConfirmModalRef } from './ConfirmModal';
import NoContentModal, { NoContentModalRef } from './NoContentModal';

function AssignedStudentsTable(props: { teacherId: string }) {
  const { teacherId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation(
    TranslationsKeys.TEACHER_MANAGMENT_TEACHER_FILE_ASSIGNED_STUDENTS,
  );
  const {
    data,
    loading,
    query,
    updateQuery,
    refresh,
  } = useAssignedStudentsTableLoader();

  const deleteModalRef = useRef<ConfirmModalRef>(null);
  const confirmRef = useRef<NoContentModalRef>(null);
  const addRef = useRef<AssignedStudentModalRef>(null);

  const { unassigned } = useSharedData();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const actions = useActions({
    onSuccess: () => {
      addToast({
        icon: 'success',
        color: 'success',
        text: 'texts.modal.success',
      });
    },
    onError: (_, messsage) => {
      addToast({
        icon: 'error',
        color: 'danger',
        text: messsage ?? 'Ha ocurrido un error',
      });
    },
  });

  const columns = useColumns();

  const requestDownload = async () => {
    setIsLoading(true);
    const res = await getReport(teacherId, query);
    if (res.error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: 'Error al descargar la plantilla',
      });
      setIsLoading(false);
      return;
    }
    if (res.status === 204) {
      confirmRef.current?.openModal();
      setIsLoading(false);
      return;
    }
    const blob = new Blob([res.data], { type: 'vnd.ms-excel' });
    const fileName = `Planilla_de_estudiantes_asignados.xlsx`;
    downloadFromBlob(blob, fileName);
    setIsLoading(false);
  };

  return (
    <div className="student-grades-closure mx-3">
      <Card className="px-4 pt-4">
        <HeaderInfo path={PathsLayouts.teacherFile as string} />
        <p className="fs-18 font-weight-bold text-uppercase text-primary mb-0">
          {t('title')}
        </p>
        <p className="mb-3 fs-16">{t('description')}</p>
        <AssignedStudentFiltersLoader>
          <AssignedStudentFilters
            onSubmit={(filters) => updateQuery({ ...query, ...filters })}
          />
        </AssignedStudentFiltersLoader>
        <Row>
          <Col>
            <div className="d-flex align-items-center">
              <Icon name="information" color="primary" className="mr-1" />
              <p className="mb-1 fs-16 font-weight-bold">
                {t('totalStudents', { count: data?.total })}
              </p>
            </div>
          </Col>
          <Col className="pb-3">
            <Button
              className="float-right"
              size="md"
              icon="download"
              text="Descargar listado"
              loading={isLoading}
              onClick={() => requestDownload()}
            />
            <Button
              className="float-right mr-2"
              size="md"
              icon="plus"
              text="Asignar Alumno"
              onClick={() => addRef.current?.openModal()}
            />
          </Col>
        </Row>
        <Table
          data={data?.data ?? []}
          columns={columns}
          isLoadingResults={loading}
          loadingView={{
            title: t('table.loadingText'),
            subtitle: t('table.loadingSubText'),
          }}
          noResultsText={
            <TableEmptyContent
              title={t('table.emptyText')}
              subtitle={t('table.emptySubText')}
            />
          }
          pagination={{
            currentPage: query.page,
            totalItems: data?.total ?? 0,
            totalPages: data?.total_pages ?? 0,
            itemsPerPage: TOTAL_ITEMS_PER_PAGE,
            onChangePage: (page: number) => updateQuery({ ...query, page }),
          }}
          footComponent={() => <Col style={{ minHeight: '70px' }} />}
        />
        <Col>
          <Button
            disabled={unassigned.length === 0}
            className="float-right mr-2 mb-3"
            size="lg"
            text="DESASIGNAR SELECCIONADOS"
            onClick={() => deleteModalRef.current?.openModal(Number(teacherId))}
          />
        </Col>
      </Card>
      <NoContentModal ref={confirmRef} />
      <AssignedStudentModal ref={addRef} onConfirm={() => refresh()} />
      <ConfirmModal
        onDecline={() => deleteModalRef.current?.closeModal()}
        onConfirm={() => refresh()}
        ref={deleteModalRef}
      />
    </div>
  );
}

export default AssignedStudentsTable;
